import React from "react"
import {
  ShareV2,
  FacebookIcon,
  PinterestIcon,
  EmailIcon,
  WhatsAppIcon,
} from "components/SvgIcons"
import { TwitterNew } from "components/SvgIcons/social-media/twitter-new"
import { space } from "theme/themeConfig"
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverBody,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  Flex,
  Text,
} from "@chakra-ui/react"
import { SocialShareLink } from "packages/ui-shared/components/SocialShareLink"
import type { VariantType } from "@shared/graphql.types"
import { siteUrl } from "utils/getSiteUrl"
import { useAdvocateCode, useLocale } from "hooks"
import { trackProductShared } from "utils/gtm"
import { getUrl } from "components/Layouts/Header/Header.utils"
import { trackSegmentProductShared } from "data/tracking/segment/track/trackSegmentProductShared"
import { useUser } from "lib/user"
import { Link } from "components/Link"
import { CopyButton } from "components/Shop/PDPPage/PDPHeader/Pricing/ShareProduct/CopyButton"
import { usePathname } from "next/navigation"

type Props = {
  variant: VariantType
}

export const ShareProductPDP = ({ variant }: Props): JSX.Element => {
  const locale = useLocale()
  const { hasMembership } = useUser()
  const { advocateCode = "" } = useAdvocateCode()
  const pathname = usePathname() ?? ""

  const slug = `${locale === "en-US" ? "/us" : ""}/product/${variant.slug}`

  const getSocialLink = (social: string, code?: string) => {
    if (!code) return getUrl(locale, pathname)
    const shareSocialUrl = `${siteUrl}${
      locale === "en-US" ? "/us" : ""
    }/redeem-referral?redirectUrl=${slug}&referralCode=${code}?utm_source=advocate&utm_medium=PDP&utm_campaign=${social}`

    return encodeURIComponent(shareSocialUrl)
  }

  const iconSizes = { h: "1.8rem", w: "1.8rem" }

  const { sku, title, brandName, id, pricing, media } = variant

  const imageToShare = media?.[0]?.thumbnails?.size_OTF_BETA

  const handleShare = async (event) => {
    trackProductShared()
    trackSegmentProductShared({
      sku,
      name: title,
      brand: brandName,
      variant: id ?? "",
      price: hasMembership ? variant.pricing.members : variant.pricing.typical,
      membersPrice: pricing.members,
      typicalPrice: pricing.typical,
      currency: locale === "en-US" ? "USD" : "GBP",
      url: slug,
    })
    if (navigator.share) {
      event.preventDefault()
      try {
        await navigator.share({
          url: window.location.href,
        })
        return
      } catch (error) {
        console.error("Error sharing:", error)
      }
    }
  }

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <Button
          justifyContent="flex-start"
          minHeight={space[6.5]}
          leftIcon={<ShareV2 h="1rem" w="1rem" />}
          variant="link"
          onClick={handleShare}
          mt={{ base: 1, md: 0 }}
          textDecoration="none"
          backgroundColor="inherit"
          aria-label="Share"
        />
      </PopoverTrigger>

      <PopoverContent maxH="12.1875rem" maxW="15.625rem" p={1}>
        <PopoverArrow />
        <PopoverCloseButton p={0} right="0.75rem" />
        <PopoverHeader border="0" p={0}>
          <Text mt={0} variant="acuminRegular">
            Share
          </Text>
        </PopoverHeader>
        <PopoverBody p={0}>
          <Flex
            w="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            height="auto"
            pt={1}
            pb={space["-1"]}
          >
            <SocialShareLink
              slug={getSocialLink("Facebook", advocateCode)}
              network="facebook"
            >
              <FacebookIcon {...iconSizes} />
            </SocialShareLink>

            <SocialShareLink
              slug={getSocialLink("Twitter", advocateCode)}
              network="twitter"
            >
              <TwitterNew {...iconSizes} />
            </SocialShareLink>

            <SocialShareLink
              slug={getSocialLink("Pinterest", advocateCode)}
              network="pinterest"
              image={imageToShare ?? ""}
            >
              <PinterestIcon {...iconSizes} />
            </SocialShareLink>

            <SocialShareLink
              slug={getSocialLink("Whatsapp", advocateCode)}
              network="whatsapp"
            >
              <WhatsAppIcon {...iconSizes} p="0.18rem" />
            </SocialShareLink>

            <Link
              href={`mailto:?subject={subject}&body=${getSocialLink(
                "Email",
                advocateCode
              )}`}
              title="Share by Email"
            >
              <EmailIcon h="1.8rem" w="1.8rem" />
            </Link>
          </Flex>
          <CopyButton advocateCode={advocateCode} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
